import { useState } from 'react';
import type { AppView } from '../api/client';
import type { RuntimeConfig } from '../auth/oauth';
import { buildImplementationPrompt } from '../lib/implementation-prompt';

/**
 * A ready-to-paste brief for whoever is implementing sign-in in the app itself.
 *
 * Collapsed behind a link because it is a handoff artefact, not something an administrator reads:
 * the common case is opening an app, clicking this, copying, and pasting it into a builder.
 *
 * Nothing here is secret. Every value is either a public OAuth parameter or discoverable by
 * watching one sign-in redirect, which is why it can sit on the detail page without a warning.
 */
export function ImplementationPrompt({
  app,
  config,
}: {
  app: AppView;
  config: RuntimeConfig;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const prompt = buildImplementationPrompt(app, config);

  return (
    <div className="field">
      <button
        type="button"
        className="linklike"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide implementation prompt' : 'Implementation prompt'}
      </button>

      {open && (
        <>
          <span className="hint">
            Paste this into an LLM builder, or hand it to a developer, to implement sign-in for this
            app. It contains no secrets.
          </span>
          <div className="copyrow copyrow-block">
            <textarea
              className="prompt"
              readOnly
              rows={14}
              value={prompt}
              aria-label={`Implementation prompt for ${app.app_name}`}
              // Selects the whole brief on focus, so keyboard copying does not mean shift-arrowing
              // through fourteen lines.
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(prompt).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
